
import React, { Suspense } from "react";
import lazyWithRetry from "lazyWithRetry";
import AppErrorPage from "pages/AppErrorPage";
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";

import allRoutes from "client/constants/allRoutes";

// Layouts — keep eagerly loaded (needed immediately)
import PublicLayout from "marketing/layouts/PublicLayout";
import LayoutWrapper from "portal/pages/Layout/Layout";
import SuperAdminLayout from "portal/pages/SuperAdmin/SuperAdminLayout";
import SuperAdminGuard from "portal/pages/SuperAdmin/SuperAdminGuard";
import ProtectedRoute from "ProtectedRoute";
import ProtectedRouteAuth from "ProtectedRouteAuth";
import PermissionRoute from "portal/components/PermissionRoute";

// Loading fallback.
// Reserves a full viewport so the layout's footer starts below the fold while the
// route chunk loads. At 60vh the footer painted inside the viewport and then got
// pushed down when the real page mounted — a single ~0.32 layout shift, which was
// almost all of the landing page's CLS.
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <div style={{ textAlign: 'center', color: '#888' }}>Loading...</div>
  </div>
);

// ─── Lazy-loaded pages ───────────────────────────────────────────────────────

// Marketing / Public
const LandingPage = lazyWithRetry(() => import("marketing/pages/LandingPage"));
const FeaturesPage = lazyWithRetry(() => import("marketing/pages/FeaturesPage"));
const PricingPage = lazyWithRetry(() => import("marketing/pages/PricingPage"));
const IntegrationsPage = lazyWithRetry(() => import("marketing/pages/IntegrationsPage"));
const SecurityPage = lazyWithRetry(() => import("marketing/pages/SecurityPage"));
const AboutPage = lazyWithRetry(() => import("marketing/pages/AboutPage"));
const ContactPage = lazyWithRetry(() => import("marketing/pages/ContactPage"));
const SupportPage = lazyWithRetry(() => import("marketing/pages/SupportPage"));
const PrivacyPage = lazyWithRetry(() => import("marketing/pages/PrivacyPage"));
const TermsPage = lazyWithRetry(() => import("marketing/pages/TermsPage"));

// Auth
const Login = lazyWithRetry(() => import("portal/pages/Login/login"));
const Signup = lazyWithRetry(() => import("portal/pages/Login/signup"));
const ForgotPassword = lazyWithRetry(() => import("portal/pages/Login/forgotPassword"));
const OTPScreen = lazyWithRetry(() => import("portal/pages/Login/OTPScreen"));
const ResetPassword = lazyWithRetry(() => import("portal/pages/Login/resetPassword"));
const ClinicChooser = lazyWithRetry(() => import("portal/pages/Login/ClinicChooser"));
const ForceChangePassword = lazyWithRetry(() => import("portal/pages/Login/ForceChangePassword"));

// Core pages
const Dashboard = lazyWithRetry(() => import("portal/pages/Dashboard/Dashboard"));
const NotFoundPage = lazyWithRetry(() => import("pages/NotFoundPage"));

// Patient
const AddPatient = lazyWithRetry(() => import("portal/pages/Patient/addpatient"));
const AllPatient = lazyWithRetry(() => import("portal/pages/Patient/allpatient"));
const GuestList = lazyWithRetry(() => import("portal/pages/Patient/GuestList"));
const WalletCredits = lazyWithRetry(() => import("portal/pages/Patient/WalletCredits"));
const Referrals = lazyWithRetry(() => import("portal/pages/Patient/Referrals"));
const PatientProfile = lazyWithRetry(() => import("portal/pages/Patient/patientprofile"));

// Employee
const EmployeeList = lazyWithRetry(() => import("portal/pages/Employee/EmployeeList"));
const AddEmployee = lazyWithRetry(() => import("portal/pages/Employee/AddEmployee"));
const EmployeeProfile = lazyWithRetry(() => import("portal/pages/Employee/EmployeeProfile"));
const LoginAccess = lazyWithRetry(() => import("portal/pages/Employee/LoginAccess"));

// Profile & Settings
const Profile = lazyWithRetry(() => import("portal/pages/Profile/Profile"));
const AccountInformation = lazyWithRetry(() => import("portal/pages/Profile/AccountInformation"));
const Notifications = lazyWithRetry(() => import("portal/pages/Profile/Notifications"));
const Payment = lazyWithRetry(() => import("portal/pages/Profile/Payment"));
const PaymentHistory = lazyWithRetry(() => import("portal/pages/Profile/PaymentHistory"));
const Settings = lazyWithRetry(() => import("portal/pages/Profile/Settings"));
const ServicesPage = lazyWithRetry(() => import("portal/pages/Profile/ServicesPage"));
const SessionTypesPage = lazyWithRetry(() => import("portal/pages/Profile/SessionTypesPage"));
const DesignationsPage = lazyWithRetry(() => import("portal/pages/Profile/DesignationsPage"));
const LoginHistoryPage = lazyWithRetry(() => import("portal/pages/Profile/LoginHistoryPage"));
const ChangePasswordPage = lazyWithRetry(() => import("portal/pages/Profile/ChangePasswordPage"));
const ClinicDetailsPage = lazyWithRetry(() => import("portal/pages/Profile/ClinicDetailsPage"));
const MyClinics = lazyWithRetry(() => import("portal/pages/Profile/MyClinics"));
const DowngradeChooser = lazyWithRetry(() => import("portal/pages/Profile/DowngradeChooser"));
const BusinessHoursPage = lazyWithRetry(() => import("portal/pages/Profile/BusinessHoursPage"));
const AppointmentSettingsPage = lazyWithRetry(() => import("portal/pages/Profile/AppointmentSettingsPage"));
const TaxSettingsPage = lazyWithRetry(() => import("portal/pages/Profile/TaxSettingsPage"));
const InvoiceSettingsPage = lazyWithRetry(() => import("portal/pages/Profile/InvoiceSettingsPage"));
const IntegrationSettingsPage = lazyWithRetry(() => import("portal/pages/Profile/IntegrationSettingsPage"));
const BrandingPage = lazyWithRetry(() => import("portal/pages/Profile/BrandingPage"));
const PreferencesPage = lazyWithRetry(() => import("portal/pages/Profile/PreferencesPage"));
const HelpSupportPage = lazyWithRetry(() => import("portal/pages/Profile/HelpSupportPage"));

// Finance
const Income = lazyWithRetry(() => import("portal/pages/Income/Income"));
const Expense = lazyWithRetry(() => import("portal/pages/Expense/Expense"));
const Invoice = lazyWithRetry(() => import("portal/pages/Invoice/Invoice"));

// Appointment
const Appointment = lazyWithRetry(() => import("portal/pages/Appointment/InnerAppointment"));
const GroupSessions = lazyWithRetry(() => import("portal/pages/Appointment/GroupSessions"));

// Waiting List
const WaitingList = lazyWithRetry(() => import("portal/pages/WaitingList/WaitingList"));

// Roles
const RoleList = lazyWithRetry(() => import("portal/pages/Role/RoleList"));
const AddRole = lazyWithRetry(() => import("portal/pages/Role/AddRole"));

// Intelligence
const TherapyIntelligence = lazyWithRetry(() => import("portal/pages/TherapyIntelligence/TherapyIntelligence"));
const PredictiveHub = lazyWithRetry(() => import("portal/pages/PredictiveIntelligence/PredictiveHub"));
const RevenueForecastPage = lazyWithRetry(() => import("portal/pages/PredictiveIntelligence/RevenueForecastPage"));
const DropoutRiskPage = lazyWithRetry(() => import("portal/pages/PredictiveIntelligence/DropoutRiskPage"));
const TherapistPerformancePage = lazyWithRetry(() => import("portal/pages/PredictiveIntelligence/TherapistPerformancePage"));
const BusinessAdvisorPage = lazyWithRetry(() => import("portal/pages/PredictiveIntelligence/BusinessAdvisorPage"));
const DataHealthPage = lazyWithRetry(() => import("portal/pages/PredictiveIntelligence/DataHealthPage"));

// SaaS / Super Admin
const SuperAdminDashboard = lazyWithRetry(() => import("portal/pages/SaaS/SuperAdminDashboard"));
const ClinicsList = lazyWithRetry(() => import("portal/pages/SaaS/ClinicsList"));
const ClinicDetails = lazyWithRetry(() => import("portal/pages/SaaS/ClinicDetails"));
const PlanManager = lazyWithRetry(() => import("portal/pages/SaaS/PlanManager"));
const SubscriptionManager = lazyWithRetry(() => import("portal/pages/SaaS/SubscriptionManager"));
const SaaSAnalytics = lazyWithRetry(() => import("portal/pages/SaaS/SaaSAnalytics"));
const CrossBranchIntelligence = lazyWithRetry(() => import("portal/pages/SaaS/CrossBranchIntelligence"));
const ActivityLogs = lazyWithRetry(() => import("portal/pages/SaaS/ActivityLogs"));
const UsageStats = lazyWithRetry(() => import("portal/pages/SaaS/UsageStats"));
const PaymentFailures = lazyWithRetry(() => import("portal/pages/SaaS/PaymentFailures"));
const SupportTickets = lazyWithRetry(() => import("portal/pages/SaaS/SupportTickets"));
const PlanCheckout = lazyWithRetry(() => import("portal/pages/SaaS/PlanCheckout"));
const TestimonialsManager = lazyWithRetry(() => import("portal/pages/SaaS/TestimonialsManager"));
const NewsletterSubscribers = lazyWithRetry(() => import("portal/pages/SaaS/NewsletterSubscribers"));

// Payroll
const SalaryManagement = lazyWithRetry(() => import("portal/pages/Payroll/SalaryManagement"));
const DeviceManager = lazyWithRetry(() => import("portal/pages/Payroll/DeviceManager"));

// Leave Management
const LeaveManagement = lazyWithRetry(() => import("portal/pages/Leave/LeaveManagement"));

// Attendance Management
const AttendanceManagement = lazyWithRetry(() => import("portal/pages/Attendance/AttendanceManagement"));

// Billing (Phase 7)
const MonthlySettlement = lazyWithRetry(() => import("portal/pages/Billing/MonthlySettlement"));
const PricingPlans = lazyWithRetry(() => import("portal/pages/Billing/PricingPlans"));

// ─── Suspense wrapper helper ─────────────────────────────────────────────────
const S = ({ children }) => <Suspense fallback={<PageLoader />}>{children}</Suspense>;

const routes = createBrowserRouter(
  createRoutesFromElements(
    // A pathless wrapper purely for errorElement: anything a route throws —
    // most often a ChunkLoadError from a tab left open across a deploy — lands
    // on our own screen instead of React Router's developer stack trace.
    <Route errorElement={<AppErrorPage />}>
      {/* Marketing / Public website */}
      <Route path={allRoutes.landing} element={<PublicLayout />}>
        <Route index element={<S><LandingPage /></S>} />
        <Route path={allRoutes.features} element={<S><FeaturesPage /></S>} />
        <Route path={allRoutes.pricing} element={<S><PricingPage /></S>} />
        <Route path={allRoutes.integrations} element={<S><IntegrationsPage /></S>} />
        <Route path={allRoutes.security} element={<S><SecurityPage /></S>} />
        <Route path={allRoutes.about} element={<S><AboutPage /></S>} />
        <Route path={allRoutes.contact} element={<S><ContactPage /></S>} />
        <Route path={allRoutes.support} element={<S><SupportPage /></S>} />
        <Route path={allRoutes.privacy} element={<S><PrivacyPage /></S>} />
        <Route path={allRoutes.terms} element={<S><TermsPage /></S>} />
      </Route>

      <Route
        path={allRoutes.login}
        element={<ProtectedRoute element={<S><Login /></S>} />}
      />
      <Route
        path={allRoutes.signup}
        element={<ProtectedRoute element={<S><Signup /></S>} />}
      />
      <Route
        path={allRoutes.forgotPassword}
        element={<ProtectedRoute element={<S><ForgotPassword /></S>} />}
      />
      <Route
        path={allRoutes.otp}
        element={<ProtectedRoute element={<S><OTPScreen /></S>} />}
      />
      <Route
        path={allRoutes.resetPassword}
        element={<ProtectedRoute element={<S><ResetPassword /></S>} />}
      />
      <Route
        path={allRoutes.clinicChooser}
        element={<S><ClinicChooser /></S>}
      />
      <Route
        path={allRoutes.forceChangePassword}
        element={<S><ForceChangePassword /></S>}
      />

      {/* Portal */}
      <Route path={allRoutes.portal} element={<LayoutWrapper />}>
        <Route index element={<S><Dashboard /></S>} />
        <Route path={allRoutes.addpatient} element={<S><AddPatient /></S>} />
        <Route path={allRoutes.allpatient} element={<S><AllPatient /></S>} />
        <Route path={allRoutes.guests} element={<S><GuestList /></S>} />
        {/* Whose wallet got money in a month: the front desk sees it too, as Monthly Settlement */}
        <Route path={allRoutes.walletCredits} element={<PermissionRoute permission={["manage_billing", "manage_invoices"]} element={<S><WalletCredits /></S>} />} />
        <Route path={allRoutes.referrals} element={<PermissionRoute permission="view_referrals" element={<S><Referrals /></S>} />} />
        <Route path={allRoutes.patientprofile} element={<S><PatientProfile /></S>} />
        <Route path={allRoutes.employeelist} element={<PermissionRoute permission="view_employees" element={<S><EmployeeList /></S>} />} />
        <Route path={allRoutes.loginAccess} element={<PermissionRoute permission="view_employees" element={<S><LoginAccess /></S>} />} />
        <Route path={allRoutes.addemployee} element={<PermissionRoute permission={["manage_employees", "edit_employees"]} element={<S><AddEmployee /></S>} />} />
        <Route path={allRoutes.employeeprofile} element={<PermissionRoute permission="view_employees" element={<S><EmployeeProfile /></S>} />} />
        <Route path={allRoutes.salaryManagement} element={<PermissionRoute permission="view_salary" element={<S><SalaryManagement /></S>} />} />
        <Route path={allRoutes.deviceManager} element={<PermissionRoute permission={["manage_employees", "edit_employees"]} element={<S><DeviceManager /></S>} />} />
        <Route path={allRoutes.leaveManagement} element={<PermissionRoute permission="view_employees" element={<S><LeaveManagement /></S>} />} />
        <Route path={allRoutes.attendanceManagement} element={<PermissionRoute permission="manage_attendance" element={<S><AttendanceManagement /></S>} />} />
        <Route path={allRoutes.notifications} element={<S><Notifications /></S>} />
        <Route path={allRoutes.payment} element={<S><Payment /></S>} />
        <Route path={allRoutes.paymentHistory} element={<S><PaymentHistory /></S>} />
        <Route path={allRoutes.income} element={<PermissionRoute permission="view_income_expense" element={<S><Income /></S>} />} />
        <Route path={allRoutes.expense} element={<PermissionRoute permission="view_income_expense" element={<S><Expense /></S>} />} />
        <Route path={allRoutes.settings} element={<S><Settings /></S>} />
        <Route path={allRoutes.invoice} element={<PermissionRoute permission={["view_finance", "view_invoices"]} element={<S><Invoice /></S>} />} />
        <Route path={allRoutes.appointment} element={<S><Appointment /></S>} />
        <Route path={allRoutes.groupSessions} element={<S><GroupSessions /></S>} />
        <Route path={allRoutes.waitingList} element={<S><WaitingList /></S>} />
        <Route path={allRoutes.rolelist} element={<PermissionRoute permission="manage_settings" adminOnly element={<S><RoleList /></S>} />} />
        <Route path={allRoutes.addrole} element={<PermissionRoute permission="manage_settings" adminOnly element={<S><AddRole /></S>} />} />
        {/* Intelligence pages: view_intelligence (Admin holds all; no other role by default). Their
            data endpoints are view_analytics, which the Roles page ticks along with it. */}
        <Route path={allRoutes.therapyIntelligence} element={<PermissionRoute permission="view_intelligence" element={<S><TherapyIntelligence /></S>} />} />
        <Route path={allRoutes.predictiveIntelligence} element={<PermissionRoute permission="view_intelligence" element={<S><PredictiveHub /></S>} />} />
        <Route path={allRoutes.revenueForecast} element={<PermissionRoute permission="view_intelligence" element={<S><RevenueForecastPage /></S>} />} />
        <Route path={allRoutes.dropoutRisk} element={<PermissionRoute permission="view_intelligence" element={<S><DropoutRiskPage /></S>} />} />
        <Route path={allRoutes.therapistPerformance} element={<PermissionRoute permission="view_intelligence" element={<S><TherapistPerformancePage /></S>} />} />
        <Route path={allRoutes.businessAdvisor} element={<PermissionRoute permission="view_intelligence" element={<S><BusinessAdvisorPage /></S>} />} />
        <Route path={allRoutes.dataHealth} element={<PermissionRoute permission="view_analytics" element={<S><DataHealthPage /></S>} />} />
        <Route path={allRoutes.planCheckout} element={<S><PlanCheckout /></S>} />
        <Route path={allRoutes.billingSettlement} element={<PermissionRoute permission={["manage_billing", "manage_invoices"]} element={<S><MonthlySettlement /></S>} />} />
        <Route path={allRoutes.billingPricingPlans} element={<PermissionRoute permission="manage_billing" element={<S><PricingPlans /></S>} />} />

        <Route path={allRoutes.profile} element={<ProtectedRouteAuth element={<S><Profile /></S>} />} />
        <Route path={allRoutes.profileAccount} element={<ProtectedRouteAuth element={<S><AccountInformation /></S>} />} />
        <Route path={allRoutes.loginHistory} element={<ProtectedRouteAuth element={<S><LoginHistoryPage /></S>} />} />
        <Route path={allRoutes.changePassword} element={<ProtectedRouteAuth element={<S><ChangePasswordPage /></S>} />} />
        <Route path={allRoutes.services} element={<PermissionRoute permission="view_services" element={<S><ServicesPage /></S>} />} />
        <Route path={allRoutes.sessionTypes} element={<PermissionRoute permission="view_session_types" element={<S><SessionTypesPage /></S>} />} />
        <Route path={allRoutes.designations} element={<PermissionRoute permission="view_designations" element={<S><DesignationsPage /></S>} />} />
        <Route path={allRoutes.clinicDetails} element={<ProtectedRouteAuth element={<S><ClinicDetailsPage /></S>} />} />
        <Route path={allRoutes.myClinics} element={<ProtectedRouteAuth element={<S><MyClinics /></S>} />} />
        <Route path={allRoutes.downgradeChooser} element={<ProtectedRouteAuth element={<S><DowngradeChooser /></S>} />} />
        <Route path={allRoutes.businessHours} element={<ProtectedRouteAuth element={<S><BusinessHoursPage /></S>} />} />
        <Route path={allRoutes.appointmentSettings} element={<ProtectedRouteAuth element={<S><AppointmentSettingsPage /></S>} />} />
        {/* Tax Settings are the Admin's (owner, 2026-10-02); /admin/settings/update refuses the tax keys to anyone else. */}
        <Route path={allRoutes.taxSettings} element={<PermissionRoute permission="manage_settings" adminOnly element={<S><TaxSettingsPage /></S>} />} />
        <Route path={allRoutes.invoiceSettings} element={<ProtectedRouteAuth element={<S><InvoiceSettingsPage /></S>} />} />
        <Route path={allRoutes.integrationSettings} element={<ProtectedRouteAuth element={<S><IntegrationSettingsPage /></S>} />} />
        <Route path={allRoutes.branding} element={<ProtectedRouteAuth element={<S><BrandingPage /></S>} />} />
        <Route path={allRoutes.preferences} element={<ProtectedRouteAuth element={<S><PreferencesPage /></S>} />} />
        <Route path={allRoutes.helpSupport} element={<ProtectedRouteAuth element={<S><HelpSupportPage /></S>} />} />
      </Route>

      {/* Appointment — standalone fullscreen view (no sidebar/header chrome), opened in a new tab */}
      <Route
        path={allRoutes.appointmentFullscreen}
        element={<ProtectedRouteAuth element={<S><Appointment fullscreenMode /></S>} />}
      />

      {/* Super Admin Panel — RoleId 1 only */}
      <Route
        path={allRoutes.superadmin}
        element={
          <SuperAdminGuard>
            <SuperAdminLayout />
          </SuperAdminGuard>
        }
      >
        <Route index element={<S><SuperAdminDashboard /></S>} />
        <Route path={allRoutes.superadminClinics} element={<S><ClinicsList /></S>} />
        <Route path={allRoutes.superadminClinicDetails} element={<S><ClinicDetails /></S>} />
        <Route path={allRoutes.superadminPlans} element={<S><PlanManager /></S>} />
        <Route path={allRoutes.superadminSubscriptions} element={<S><SubscriptionManager /></S>} />
        <Route path={allRoutes.superadminRevenue} element={<S><SaaSAnalytics /></S>} />
        <Route path={allRoutes.superadminCrossBranch} element={<S><CrossBranchIntelligence /></S>} />
        <Route path={allRoutes.superadminActivityLogs} element={<S><ActivityLogs /></S>} />
        <Route path={allRoutes.superadminUsageStats} element={<S><UsageStats /></S>} />
        <Route path={allRoutes.superadminPaymentFailures} element={<S><PaymentFailures /></S>} />
        <Route path={allRoutes.superadminSupportTickets} element={<S><SupportTickets /></S>} />
        <Route path={allRoutes.superadminTestimonials} element={<S><TestimonialsManager /></S>} />
        <Route path={allRoutes.superadminNewsletter} element={<S><NewsletterSubscribers /></S>} />
      </Route>

      <Route path="*" element={<S><NotFoundPage /></S>} />
    </Route>
  ),
  { basename: "/" }
);

export default routes;
